import type { Http } from "../http";
import { parseBody } from "../models/common";
import {
  consultAnswerSchema,
  tarotReadingSchema,
  voiceTokenSchema,
  type ConsultAnswer,
  type ConsultCategory,
  type TarotCardDraw,
  type VoiceToken,
} from "../models/consult";

export interface ConsultTurnInput {
  readonly category: ConsultCategory;
  readonly question: string;
  /** Idempotency key for this turn, chosen by the client. */
  readonly turnRef: string;
  readonly language?: string;
}

/**
 * A reading waits on a model. Ten seconds — the ceiling for everything else —
 * cut off answers that were on their way; a person who has asked a real
 * question will wait longer than that for a real answer, but not forever.
 */
export const READING_TIMEOUT_MS = 45_000;

/** Typed consultation, tarot and the voice room. */
export function consultEndpoints(http: Http) {
  return {
    async consultTurn(input: ConsultTurnInput): Promise<ConsultAnswer> {
      const payload = {
        category: input.category,
        question: input.question,
        action: "quick_question",
        turn_ref: input.turnRef,
        ...(input.language ? { language: input.language } : {}),
      };
      const body = await http.post("/consult/turn", payload, { timeoutMs: READING_TIMEOUT_MS });
      return parseBody(consultAnswerSchema, body);
    },

    /** Reads a drawn spread against the chart; the shuffle is client-side. */
    async tarotReading(cards: readonly TarotCardDraw[]): Promise<readonly string[]> {
      const body = await http.post("/tarot/reading", { cards });
      return parseBody(tarotReadingSchema, body).cards.map((card) => card.reading);
    },

    /** Mints a LiveKit token for a fresh room with the agent dispatched. */
    async mintVoiceToken(): Promise<VoiceToken> {
      return parseBody(voiceTokenSchema, await http.post("/voice/token", {}));
    },
  };
}
